/**
 * Como cada provedor git se chama NA TELA — em toda a feature.
 *
 * Fica em core/ ao lado de resourceTypeLabel, e pelo mesmo motivo: o cadastro, o detalhe
 * do review e a fila de curadoria mostram o mesmo provedor, e um mapa por componente
 * divergiria no dia em que entrar o quarto provedor.
 *
 * O valor cru é identificador interno — viaja em coluna de banco, em query string e no
 * seletor do adaptador —, então é minúsculo e sem espaço. Mostrá-lo assim entrega ao
 * usuário o vocabulário do código: o grid de repositórios exibia `azuredevops` numa
 * coluna chamada "Provider", e é o produto da Microsoft escrito de um jeito que não
 * existe em nenhum lugar da documentação dele.
 *
 * A grafia é a do próprio produto, com maiúsculas e espaços como o dono da marca escreve.
 */
const LABELS: Record<string, string> = {
  tfvc: 'TFVC',
  standalone: 'Standalone',
  github: 'GitHub',
  azuredevops: 'Azure DevOps',
  gitea: 'Gitea',
  'bitbucket-cloud': 'Bitbucket Cloud'
};

/**
 * Rótulo da origem do código revisado — `tfvc`, `standalone` ou um provedor git.
 *
 * Uma função só para os dois usos. O provedor de um repositório É a origem dos reviews
 * dele, então um segundo nome para a mesma tabela de rótulos só criava a dúvida de qual
 * chamar.
 *
 * Valor desconhecido devolve o próprio texto — é feio, mas diagnosticável, e melhor que
 * uma célula em branco quando o backend passa a mandar um adaptador novo antes de o mapa
 * saber dele.
 */
export function sourceProviderLabel(provider?: string): string {
  const key = (provider || '').trim().toLowerCase();
  if (!key) { return ''; }
  return LABELS[key] ?? key;
}

/**
 * Origens oferecidas nos filtros.
 *
 * O backend ACEITA qualquer provedor cadastrável, inclusive os sem adapter ativo — ali
 * filtrar por um deles devolve lista vazia, que é a resposta certa. Esta lista é o que a
 * tela OFERECE, e por isso é mais curta: oferecer uma opção que nunca traz resultado é
 * ruído. Quando um adapter entrar, a linha correspondente entra aqui.
 */
export const SOURCE_PROVIDER_VALUES = [
  'tfvc',
  'standalone',
  'azuredevops',
  'github',
  'bitbucket-cloud',
  'gitea'
] as const;

/** Opções de select/multiselect, com o mesmo rótulo exibido nas tabelas. */
export const SOURCE_PROVIDER_OPTIONS = SOURCE_PROVIDER_VALUES.map(value => ({
  value,
  label: sourceProviderLabel(value)
}));

/**
 * Valor cru do provedor quando o que foi digitado é o nome do produto COM espaço.
 *
 * Existe porque a busca passou a rodar no servidor, e lá o provedor está gravado como
 * identificador, sem espaço (`azuredevops`, `bitbucket-cloud`). Quem lê "Azure DevOps" na
 * linha digita "Azure DevOps", e o servidor não acharia nada — esse rótulo só existe na
 * tela.
 *
 * Só traduz termo com espaço, e de propósito. Um termo de uma palavra já casa direto no
 * servidor (`git` acha `github` e `gitea`), e traduzi-lo seria pior: "git" viraria um
 * provedor só, escondendo os repositórios cujo NOME contém "git".
 *
 * Devolve string vazia quando não é nome de provedor, para quem chama seguir com o termo
 * original.
 */
export function providerKeyForTerm(term: string): string {
  const typed = (term || '').trim().toLowerCase();
  if (!typed || !/\s/.test(typed)) { return ''; }
  const collapsed = collapse(typed);
  // Compara com o RÓTULO, não com a chave: "Azure DevOps" é o que está escrito na tela,
  // e a chave (`azuredevops`) é o identificador cru que ninguém digita.
  return Object.keys(LABELS).find(key =>
    collapse(LABELS[key]).includes(collapsed) || collapse(key).includes(collapsed)) ?? '';
}

/** Tira espaço, hífen e caixa — o que separa a grafia da tela da grafia do cadastro. */
function collapse(value: string): string {
  return value.toLowerCase().replace(/[\s-]+/g, '');
}
